/**
 * Dog is Human Theme - Global JavaScript
 * Handles cart drawer, mobile menu, product variants, subscriptions
 */

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

// Cart Drawer
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.cartCount = document.querySelectorAll('.cart-count');
    this.cartItems = document.getElementById('cart-items');
    this.cartSubtotal = document.getElementById('cart-subtotal');
    this.bindEvents();
  }

  bindEvents() {
    // Support both data attributes and class-based selectors
    document.querySelectorAll('[data-cart-toggle], .js-cart-toggle').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    document.querySelectorAll('[data-cart-close], .js-cart-close').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('active')) {
        this.close();
      }
    });
  }

  open() {
    this.drawer?.classList.add('active');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.drawer?.classList.remove('active');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateUI(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  updateUI(cart) {
    this.cartCount.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    if (this.cartSubtotal) {
      this.cartSubtotal.textContent = this.formatMoney(cart.total_price);
    }

    if (this.cartItems) {
      this.cartItems.innerHTML = cart.items.map(item => this.renderItem(item)).join('');
      this.bindItemEvents();
    }
  }

  renderItem(item) {
    return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <img src="${item.image || ''}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-item__details">
          <h4>${item.product_title}</h4>
          ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          ${item.selling_plan_allocation ? '<span class="cart-item__subscription">Subscribe & Save</span>' : ''}
          <div class="cart-item__qty">
            <button data-action="decrease" data-key="${item.key}">-</button>
            <span>${item.quantity}</span>
            <button data-action="increase" data-key="${item.key}">+</button>
          </div>
        </div>
        <div class="cart-item__price">
          <span>${this.formatMoney(item.final_line_price)}</span>
          <button class="cart-item__remove" data-action="remove" data-key="${item.key}">&times;</button>
        </div>
      </div>
    `;
  }

  bindItemEvents() {
    this.cartItems?.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const action = e.currentTarget.dataset.action;
        const key = e.currentTarget.dataset.key;
        const item = this.cartItems.querySelector(`[data-key="${key}"]`);
        const qty = parseInt(item?.querySelector('.cart-item__qty span')?.textContent || 1);

        if (action === 'remove') await this.updateItem(key, 0);
        else if (action === 'decrease' && qty > 1) await this.updateItem(key, qty - 1);
        else if (action === 'increase') await this.updateItem(key, qty + 1);
      });
    });
  }

  async updateItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await response.json();
      this.updateUI(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async addItem(variantId, quantity = 1, sellingPlan = null) {
    const data = { id: variantId, quantity };
    if (sellingPlan) data.selling_plan = sellingPlan;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      this.open();
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

// Mobile Menu
class MobileMenu {
  constructor() {
    this.menu = document.getElementById('mobile-menu');
    this.overlay = document.getElementById('mobile-overlay');
    this.bindEvents();
  }

  bindEvents() {
    // Support both data attributes and class-based selectors
    document.querySelectorAll('[data-mobile-toggle], .js-menu-toggle').forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    document.querySelectorAll('[data-mobile-close], .js-menu-close').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    this.overlay?.addEventListener('click', () => this.close());

    // Accordion submenu
this.menu?.querySelectorAll('.mobile-menu__section .mobile-menu__link').forEach(trigger => {
  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    const parent = e.currentTarget.closest('.mobile-menu__section');
    const content = parent?.querySelector('.mobile-menu__submenu');
    parent?.classList.toggle('is-open');
    if (content) {
      content.style.maxHeight = parent.classList.contains('is-open') 
        ? content.scrollHeight + 'px' 
        : '0';
    }
  });
});

  }

  toggle() {
  this.menu?.classList.contains('is-active') ? this.close() : this.open();
}


  open() {
  this.menu?.classList.add('is-active');
  this.overlay?.classList.add('is-active');
  document.body.style.overflow = 'hidden';
}


  close() {
  this.menu?.classList.remove('is-active');
  this.overlay?.classList.remove('is-active');
  document.body.style.overflow = '';
}

}

// Product Form with Subscription Support
class ProductForm {
  constructor(form) {
    this.form = form;
   const productJson = document.getElementById('product-json');
this.productData = productJson ? JSON.parse(productJson.textContent) : {};


    this.selectedSellingPlan = null;
    this.bindEvents();
  }

  bindEvents() {
    // Flavor/variant selection
    this.form.querySelectorAll('[data-variant-option]').forEach(option => {
  option.addEventListener('click', (e) => {
    this.form.querySelectorAll('[data-variant-option]').forEach(o => {
      o.classList.remove('active');
      o.classList.remove('is-selected');
    });
    e.currentTarget.classList.add('active');
    e.currentTarget.classList.add('is-selected');
    
    const variantId = e.currentTarget.dataset.variantId;
    const input = this.form.querySelector('input[name="id"]');
    if (input) input.value = variantId;
    this.updatePrice(variantId);
  });
});


    // Purchase type (one-time vs subscription)
    this.form.querySelectorAll('[data-purchase-type]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.form.querySelectorAll('.purchase-option').forEach(opt => opt.classList.remove('active'));
        e.currentTarget.closest('.purchase-option')?.classList.add('active');
        this.selectedSellingPlan = e.currentTarget.dataset.sellingPlan || null;
        this.updatePrice();
      });
    });

    // Quantity
    this.form.querySelectorAll('[data-action]').forEach(btn => {
  btn.addEventListener('click', () => {
    const input = this.form.querySelector('[name="quantity"]');
    let qty = parseInt(input?.value || 1);
    if (btn.dataset.action === 'decrease' && qty > 1) qty--;
    else if (btn.dataset.action === 'increase') qty++;
    if (input) input.value = qty;
  });
});


    // Submit
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = this.form.querySelector('input[name="id"]')?.value;
      const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);
      
      if (variantId) {
  const btn = this.form.querySelector('[type="submit"]');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Adding...';
  }

  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity })
    });
    window.location.href = '/cart';
  } catch(err) {
    console.error('Add to cart error:', err);
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    }
  }
}

    });
  }

  updatePrice(variantId) {
    variantId = variantId || this.form.querySelector('input[name="id"]')?.value;
    const variant = this.productData.variants?.find(v => v.id == variantId);
    if (!variant) return;

    let price = variant.price;
    if (this.selectedSellingPlan && variant.selling_plan_allocations) {
      const allocation = variant.selling_plan_allocations.find(
        a => a.selling_plan_id == this.selectedSellingPlan
      );
      if (allocation) price = allocation.per_delivery_price;
    }

    const priceEl = document.querySelector('[data-price]');
const compareEl = document.querySelector('[data-compare-price]');

    
    if (priceEl) priceEl.textContent = '$' + (price / 100).toFixed(2);
    const btnPrice = document.getElementById('cart-btn-price');
if (btnPrice) btnPrice.textContent = '$' + (price / 100).toFixed(2);

    if (compareEl) {
  if (variant.compare_at_price && variant.compare_at_price > price) {
    compareEl.textContent = '$' + (variant.compare_at_price / 100).toFixed(2);
    compareEl.style.display = 'inline';
    const saveBadge = document.querySelector('.price-badge');
    if (saveBadge) {
      const saved = (variant.compare_at_price - price) / 100;
      saveBadge.textContent = 'SAVE $' + saved.toFixed(2);
      saveBadge.style.display = 'inline';
    }
  } else {
    compareEl.style.display = 'none';
    const saveBadge = document.querySelector('.price-badge');
    if (saveBadge) saveBadge.style.display = 'none';
  }
}

  }
}

// Product Gallery
class ProductGallery {
  constructor(container) {
    this.container = container;
    this.mainImage = container.querySelector('.gallery__main img');
    this.thumbs = container.querySelectorAll('.gallery__thumb');
    
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src || thumb.querySelector('img')?.src;
        if (this.mainImage && src) this.mainImage.src = src;
        this.thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }
}

// Reviews Carousel
class ReviewsCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.reviews__track');
    this.slides = container.querySelectorAll('.review-card');
    this.index = 0;
    
    container.querySelector('[data-prev]')?.addEventListener('click', () => this.prev());
    container.querySelector('[data-next]')?.addEventListener('click', () => this.next());
    
    // Touch
    let startX = 0;
    this.track?.addEventListener('touchstart', e => startX = e.touches[0].clientX);
    this.track?.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? this.next() : this.prev();
    });
  }

  prev() {
    this.index = Math.max(0, this.index - 1);
    this.update();
  }

  next() {
    const visible = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
    this.index = Math.min(this.slides.length - visible, this.index + 1);
    this.update();
  }

  update() {
    const width = this.slides[0]?.offsetWidth || 0;
    if (this.track) this.track.style.transform = `translateX(-${this.index * (width + 24)}px)`;
  }
}

// Accordion
class Accordion {
  constructor(container) {
    container.querySelectorAll('.accordion__item').forEach(item => {
      item.querySelector('.accordion__trigger')?.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        container.querySelectorAll('.accordion__item').forEach(i => {
          i.classList.remove('is-open');
          const c = i.querySelector('.accordion__content');
          if (c) c.style.maxHeight = '0';
        });
        if (!isOpen) {
          item.classList.add('is-open');
          const content = item.querySelector('.accordion__content');
          if (content) content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }
}

// Init
document.addEventListener('DOMContentLoaded', () => {
  window.cart = new CartDrawer();
  new MobileMenu();
  
  const productForm = document.getElementById('product-form');
if (productForm) new ProductForm(productForm);

  document.querySelectorAll('.product-gallery').forEach(g => new ProductGallery(g));
  // 产品页缩略图切换
const thumbnails = document.querySelectorAll('.product-page__thumbnail');
const mainImages = document.querySelectorAll('.product-page__main-image');

thumbnails.forEach(thumb => {
  thumb.addEventListener('click', () => {
    const index = thumb.dataset.imageIndex;

    // 切换缩略图选中状态
    thumbnails.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');

    // 切换主图显示
    mainImages.forEach(img => img.classList.remove('is-active'));
    const target = document.querySelector(`.product-page__main-image[data-image-index="${index}"]`);
    if (target) target.classList.add('is-active');
  });
});

  document.querySelectorAll('.reviews-carousel').forEach(c => new ReviewsCarousel(c));
  document.querySelectorAll('.accordion').forEach(a => new Accordion(a));
  
  // Smooth scroll
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const href = a.getAttribute('href');
      if (href !== '#') {
        e.preventDefault();
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
});

// Sticky header
let lastScroll = 0;
window.addEventListener('scroll', debounce(() => {
  const header = document.querySelector('.header');
  const scroll = window.pageYOffset;
  
  header?.classList.toggle('is-scrolled', scroll > 100);
  header?.classList.toggle('is-hidden', scroll > lastScroll && scroll > 200);
  lastScroll = scroll;
}, 10));
